/* Shown when a trial org's 14 days are up. The API returns 403 TRIAL_EXPIRED for every
   authenticated call, so there's nothing to show but the way back to us. */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, PhoneCall, LogOut, CreditCard } from "lucide-react";

const CONTACT = "admin@armorbatch.com";

export default function TrialExpired() {
  const [busy, setBusy] = useState(false);
  // Billing endpoints are exempt from the trial gate, so they can pay their way back in.
  const subscribe = async () => {
    setBusy(true);
    try {
      let data;
      try {
        ({ data } = await api.post("/billing/checkout-tier", { origin_url: window.location.origin }));
      } catch (e) {
        // No tier assigned yet → fall back to the per-truck plan so they can still pay.
        ({ data } = await api.post("/billing/checkout", { origin_url: window.location.origin }));
      }
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not open checkout — email admin@armorbatch.com and we'll sort it");
      setBusy(false);
    }
  };
  const signOut = async () => {
    try { await api.post("/auth/logout"); } catch { /* cookie may already be gone */ }
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="trial-expired">
      <div className="max-w-xl">
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">ArmorBatch Global</div>
        <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 leading-[1.05]">
          Your trial has ended.
        </h1>
        <p className="mt-5 text-base text-zinc-600">
          Your plant, dockets and history are all still here — nothing has been deleted. Get in touch and
          we'll switch you back on, usually the same day.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button type="button" onClick={subscribe} disabled={busy} data-testid="trial-expired-subscribe"
            className="inline-flex items-center justify-center gap-2.5 bg-[#1D4ED8] hover:bg-[#1A43BA] disabled:opacity-50 text-white text-base font-bold px-7 py-3.5 transition-colors">
            <CreditCard className="w-4.5 h-4.5" /> {busy ? "Opening checkout…" : "Subscribe and switch back on"}
          </button>
          <a href={`mailto:${CONTACT}?subject=${encodeURIComponent("ArmorBatch — continue after trial")}`}
            data-testid="trial-expired-contact"
            className="inline-flex items-center justify-center gap-2 border border-zinc-300 hover:border-zinc-900 text-zinc-800 text-sm font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
            <Mail className="w-4 h-4" /> Contact us
          </a>
          <a href="tel:+61400597850" data-testid="trial-expired-call"
            className="inline-flex items-center justify-center gap-2 border border-zinc-300 hover:border-zinc-900 text-zinc-800 text-sm font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
            <PhoneCall className="w-4 h-4" /> Call us
          </a>
          <button type="button" onClick={signOut} data-testid="trial-expired-signout"
            className="inline-flex items-center justify-center gap-2 text-zinc-500 hover:text-zinc-900 text-xs font-bold uppercase tracking-widest px-3 py-3.5 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
